import {useEffect, useMemo, useRef} from "react";
import {useForm, useWatch} from "react-hook-form";

import {Button} from "@/components/ui/button";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {FakeProgress} from "@/components/ui/fake-progress";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import ImagePicker from "@/components/ui/image-picker";
import {Input} from "@/components/ui/input";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";
import {Sheet, SheetContent, SheetFooter} from "@/components/ui/sheet";
import {Switch} from "@/components/ui/switch";
import {Textarea} from "@/components/ui/textarea";
import {
  useDispatchLogTypes,
  useDispatchLogCreate,
  useDispatchLogUpdate,
} from "@/src/modules/dispatch-logs/hooks";
import {
  deserializeDispatchLogFormDraftValues,
  dispatchLogHasPersistableContent,
  DISPATCH_LOG_SHEET_ID,
  getDispatchLogFormDraftKey,
  resolveDispatchLogDraftProperty,
  resolveDispatchLogDraftType,
  serializeDispatchLogFormDraftValues,
} from "@/src/modules/dispatch-logs/lib/dispatch-log-form-draft";
import {Loader} from "@/src/modules/core/components";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {useFormDraft, type ClearFormDraft} from "@/src/modules/core/hooks/use-form-draft";
import {useRestoreFormDraft} from "@/src/modules/core/hooks/use-restore-form-draft";
import {createFormInvalidHandler} from "@/src/modules/core/lib/glitchtip/product-telemetry";
import {useStoredMeProperties} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {useCreateFiles} from "@/src/modules/files/hooks";
import {HouseholdCombobox} from "@/src/modules/households/components/combobox";
import {FieldHelpLabel} from "@/src/modules/help/components/field-help";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";
import {useCancelHelpTourOnSheetClose} from "@/src/modules/help/hooks/use-cancel-help-tour-on-sheet-close";
import {
  candidateToHouseholdShape,
  getFirstResidentDisplayName,
} from "@/src/modules/dispatch/lib/apply-household-assist-candidate";
import {useDispatchDeskHouseholdMatch} from "@/src/modules/dispatch/store/desk-household-match";

import {
  DISPATCH_LOG_FORM_HELP_KEYS,
  DISPATCH_LOG_HELP_TOURS,
} from "../lib/dispatch-log-help";

export function DispatchLogsSheet({
  isVisible,
  dispatchLogId,
  onClose,
  draftRestoreValues,
  onDraftRestored,
}: {
  isVisible: boolean;
  dispatchLogId: number | null;
  onClose: () => void;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
}) {
  const draftHelpersRef = useRef<{clearDraft: ClearFormDraft} | null>(null);
  const skipOpenResetRef = useRef(false);
  const userProperty = useStoredUserProperty();
  const userProperties = useStoredMeProperties();

  const properties = userProperties.filter((p: any) => p.is_hidden === 0);
  const hasPropertySelector = properties.length > 1;

  const {dispatchLogTypes: _dispatchLogTypes, isPending: isDispatchLogTypesPending} =
    useDispatchLogTypes();
  const dispatchLogTypes = useMemo(
    () =>
      _dispatchLogTypes?.map((t: {type: string; dispatch_log_type_id: number}) => ({
        label: t.type,
        value: String(t.dispatch_log_type_id),
      })) ?? [],
    [_dispatchLogTypes],
  );
  const isPending = isDispatchLogTypesPending;

  const {mutateAsync: mutateCreateFiles} = useCreateFiles();
  const {mutateAsync: createDispatchLog, isPending: isCreateDispatchLogPending} =
    useDispatchLogCreate();
  const {mutateAsync: updateDispatchLog, isPending: isUpdateDispatchLogPending} =
    useDispatchLogUpdate();

  const defaultValues = {
    files: [] as any[],
    property: userProperty,
    house_hold: null as any,
    dispatch_log_type: null as any,
    name: "",
    callback_number: "",
    details: "",
    send_notification: false,
    create_automatic_dar: false,
  };

  const form = useForm({
    defaultValues,
  });
  const {
    watch,
    setValue,
    getValues,
    control,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting: _isSubmitting},
  } = form;
  const files = useWatch({control, name: "files", defaultValue: []});
  const property = useWatch({control, name: "property"});
  const prevPropertyIdRef = useRef<number | undefined>(property?.id);

  const isEditing = dispatchLogId != null;
  const deskHouseholdMatch = useDispatchDeskHouseholdMatch();
  useCancelHelpTourOnSheetClose(isVisible);
  const draftKey = getDispatchLogFormDraftKey(isEditing, dispatchLogId, userProperty?.id);

  const {snapshotDraft, clearDraft} = useFormDraft({
    draftKey,
    moduleLabel: "Dispatch log",
    route: "/dispatchlogs",
    sheetId: DISPATCH_LOG_SHEET_ID,
    entityId: dispatchLogId ?? undefined,
    contextPropertyId: userProperty?.id,
    enabled: isVisible,
    getValues,
    serializeValues: serializeDispatchLogFormDraftValues,
    shouldPersist: () => dispatchLogHasPersistableContent(getValues()),
    watch,
    abandonTelemetry: {
      feature: "dispatchlogs",
      operation: isEditing ? "dispatch_log_update" : "dispatch_log_create",
    },
  });

  useEffect(() => {
    draftHelpersRef.current = {clearDraft};
  }, [clearDraft]);

  useRestoreFormDraft({
    draftRestoreValues,
    when: dispatchLogTypes.length > 0,
    onRestore: () => {
      const restored = deserializeDispatchLogFormDraftValues(draftRestoreValues!);
      const fullProperty = resolveDispatchLogDraftProperty(
        (restored as {property?: typeof defaultValues.property}).property,
        userProperties,
        userProperty!,
      );
      const resolvedType = resolveDispatchLogDraftType(restored.dispatch_log_type, dispatchLogTypes);
      reset({
        ...defaultValues,
        ...restored,
        property: fullProperty,
        dispatch_log_type: resolvedType ?? restored.dispatch_log_type,
      });
      skipOpenResetRef.current = true;
      onDraftRestored?.();
    },
  });

  const isSubmitting =
    isCreateDispatchLogPending || isUpdateDispatchLogPending || _isSubmitting;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    draftHelpersRef.current?.clearDraft();
    onClose();
  };

  const onSubmit = async (data: any) => {
    const mutate = dispatchLogId ? updateDispatchLog : createDispatchLog;

    let processedFiles = data.files.filter((i: any) => !(i instanceof File));
    const toProcess = data.files.filter((i: any) => i instanceof File);

    if (toProcess.length && navigator.onLine) {
      const newFiles = await mutateCreateFiles(toProcess);
      processedFiles = [...processedFiles, ...newFiles];
    } else if (!navigator.onLine) {
      processedFiles = data.files;
    }

    const typeValue = data.dispatch_log_type?.value;
    const payload = {
      ...data,
      property_id: data.property.id,
      house_hold_id: data.house_hold?.house_hold_id,
      dispatch_log_type_id:
        typeValue != null && typeValue !== "" ? Number(typeValue) : undefined,
      callback_number: (data.callback_number ?? "").replace(/ /g, "").replace("-", ""),
      send_notification: data.send_notification ? 1 : 0,
      create_automatic_dar: data.create_automatic_dar ? 1 : 0,
      files: processedFiles,
    };

    if (dispatchLogId) {
      payload.dispatch_log_id = dispatchLogId;
    }

    await mutate(payload);
    draftHelpersRef.current?.clearDraft({submitted: true});
    onClose();
  };

  const handleUploadFiles = async (newFiles: any) => {
    setValue("files", newFiles, {shouldDirty: true});
    snapshotDraft();
  };

  const removeFiles = (index: number) => {
    setValue(
      "files",
      getValues("files").filter((_: any, i: number) => i !== index),
      {shouldDirty: true},
    );
    snapshotDraft();
  };

  useEffect(() => {
    const propertyId = property?.id;
    if (prevPropertyIdRef.current === propertyId) {
      return;
    }
    prevPropertyIdRef.current = propertyId;
    if (skipOpenResetRef.current) {
      skipOpenResetRef.current = false;
      return;
    }
    setValue("house_hold", null);
  }, [property?.id, setValue]);

  useEffect(() => {
    if (!isVisible || isEditing || !deskHouseholdMatch) return;
    if (String(deskHouseholdMatch.property_id) !== String(property?.id)) return;

    const currentHousehold = getValues("house_hold");
    if (!currentHousehold) {
      setValue("house_hold", candidateToHouseholdShape(deskHouseholdMatch), {shouldDirty: true});
    }

    const residentName = getFirstResidentDisplayName(deskHouseholdMatch);
    if (residentName && !getValues("name")?.trim()) {
      setValue("name", residentName, {shouldDirty: true});
    }
  }, [deskHouseholdMatch, getValues, isEditing, isVisible, property?.id, setValue]);

  return (
    <Sheet open={isVisible} askConfirmation={form.formState.isDirty} onOpenChange={handleOpenChange}>
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHelpHeader
          title={dispatchLogId ? "Update Dispatch Log" : "New Dispatch Log"}
          createTourId={DISPATCH_LOG_HELP_TOURS.formCreate}
          showCreateTour={!dispatchLogId}
        />
        {isPending ? (
          <Loader className="h-full min-h-48" />
        ) : (
          <Form {...form}>
            <form
              onSubmit={handleSubmit(
                onSubmit,
                createFormInvalidHandler({
                  feature: "dispatchlogs",
                  operation: isEditing ? "dispatch_log_update" : "dispatch_log_create",
                  sheetId: DISPATCH_LOG_SHEET_ID,
                }),
              )}
              className={FORM_SHEET_ROOT_FORM_CLASS}
            >
              <SheetFormScrollBody className="flex flex-col gap-6 px-4 pt-2 pb-4">
              {hasPropertySelector ? (
                <FormField
                  control={form.control}
                  name="property"
                  rules={{required: "Property is required"}}
                  render={({field, formState: {errors}}) => (
                    <FormItem>
                      <FieldHelpLabel helpKey={DISPATCH_LOG_FORM_HELP_KEYS.property} showIcon={false}>
                        <FormLabel>Property</FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <ComboboxGeneric
                          items={properties}
                          {...field}
                          placeholder="Select property"
                          preserveCase
                          getItemKey={(item: any) => item.id}
                          getItemLabel={(item: any) => item.name}
                          isItemEqual={(item: any, selected: any) => item.id === selected?.id}
                          renderItem={(option: any) => (
                            <Item size="sm" className="p-0">
                              <ItemContent>
                                <ItemTitle className="font-normal">{option.name}</ItemTitle>
                                <ItemDescription className="text-xs">
                                  {option.street} {option.number}, {option.city}, {option.state}{" "}
                                  {option.zipcode}
                                </ItemDescription>
                              </ItemContent>
                            </Item>
                          )}
                        />
                      </FormControl>
                      {errors.property && <FormError error={errors.property.message} />}
                    </FormItem>
                  )}
                />
              ) : null}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="house_hold"
                  render={({field}) => (
                    <FormItem>
                      <FieldHelpLabel helpKey={DISPATCH_LOG_FORM_HELP_KEYS.household} showIcon={false}>
                        <FormLabel>Household</FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <HouseholdCombobox
                          value={field.value}
                          onChange={field.onChange}
                          filter={{context_property_id: property?.id}}
                        />
                      </FormControl>
                      <FormError error={errors.house_hold?.message} />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="dispatch_log_type"
                  rules={{required: "Type is required"}}
                  render={({field}) => (
                    <FormItem>
                      <FieldHelpLabel helpKey={DISPATCH_LOG_FORM_HELP_KEYS.dispatchLogType} showIcon={false}>
                        <FormLabel>Type</FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <ComboboxGeneric
                          items={dispatchLogTypes}
                          value={field.value}
                          onChange={field.onChange}
                          getItemKey={(item: any) => item.value}
                          getItemLabel={(item: any) => item.label}
                          isItemEqual={(item: any, selected: any) => item.value === selected?.value}
                          placeholder="Select type"
                          emptyMessage="No items found."
                        />
                      </FormControl>
                      <FormError error={errors.dispatch_log_type?.message} />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="name"
                  rules={{required: "Resident is required"}}
                  render={({field}) => (
                    <FormItem>
                      <FieldHelpLabel helpKey={DISPATCH_LOG_FORM_HELP_KEYS.name} showIcon={false}>
                        <FormLabel>Resident</FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormError error={errors.name?.message} />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="callback_number"
                  rules={{
                    validate: (v: string) => {
                      if (!v) return "Callback number is required";
                      const value = (v ?? "").replace(/ /g, "").replace("-", "");
                      if (value.length !== 10) return "Invalid callback number";
                      return true;
                    },
                  }}
                  render={({field}) => (
                    <FormItem>
                      <FieldHelpLabel helpKey={DISPATCH_LOG_FORM_HELP_KEYS.callbackNumber} showIcon={false}>
                        <FormLabel>Callback Number</FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <div className="flex items-center">
                          <p className="flex size-9 min-w-9 items-center justify-center rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground shadow-xs">
                            +1
                          </p>
                          <Input
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value
                                .replace(/ /g, "")
                                .replace("-", "")
                                .replace(/[a-zA-Z]/g, "");
                              if (value.length > 10) return;
                              const formatted = value.split("");
                              const length = formatted.length;
                              if (length > 3) formatted.splice(3, 0, " ");
                              if (length >= 7) formatted.splice(7, 0, "-");
                              setValue("callback_number", formatted.join(""));
                            }}
                            className="rounded-l-none"
                          />
                        </div>
                      </FormControl>
                      <FormError error={errors.callback_number?.message} />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <FormField
                  control={control}
                  name="details"
                  rules={{required: "Details are required"}}
                  render={({field}) => (
                    <FormItem>
                      <FieldHelpLabel helpKey={DISPATCH_LOG_FORM_HELP_KEYS.details} showIcon={false}>
                        <FormLabel>Details</FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <Textarea maxLength={500} rows={5} className="resize-none" {...field} />
                      </FormControl>
                      <FormError error={errors.details?.message} />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormItem>
                  <FieldHelpLabel helpKey={DISPATCH_LOG_FORM_HELP_KEYS.images} showIcon={false}>
                    <FormLabel htmlFor="files" className="text-foreground">
                      Images
                    </FormLabel>
                  </FieldHelpLabel>
                  <ImagePicker
                    images={files ?? []}
                    errors={errors}
                    immediateUpload
                    captureFeature="dispatchlogs"
                    captureOperation={isEditing ? "dispatch_log_update" : "dispatch_log_create"}
                    onBeforePick={snapshotDraft}
                    onUpload={handleUploadFiles}
                    onDelete={removeFiles}
                  />
                  {files?.length > 0 && isSubmitting ? <FakeProgress /> : null}
                </FormItem>
              </div>

              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name="create_automatic_dar"
                  render={({field}) => (
                    <FormItem className="flex flex-row items-center justify-between">
                      <FieldHelpLabel
                        helpKey={DISPATCH_LOG_FORM_HELP_KEYS.createAutomaticDar}
                        watchValue={field.value}
                        showIcon={false}
                      >
                        <FormLabel htmlFor="create_automatic_dar" className="text-foreground">
                          Create an automatic DAR
                        </FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <Switch
                          id="create_automatic_dar"
                          name="create_automatic_dar"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="send_notification"
                  render={({field}) => (
                    <FormItem className="flex flex-row items-center justify-between">
                      <FieldHelpLabel
                        helpKey={DISPATCH_LOG_FORM_HELP_KEYS.sendNotification}
                        watchValue={field.value}
                        showIcon={false}
                      >
                        <FormLabel htmlFor="send_notification" className="text-foreground">
                          Send notification to property manager
                        </FormLabel>
                      </FieldHelpLabel>
                      <FormControl>
                        <Switch
                          id="send_notification"
                          name="send_notification"
                          disabled={Boolean(dispatchLogId)}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              </SheetFormScrollBody>
              <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>
                <Button
                  type="submit"
                  disabled={isSubmitting || isPending}
                  data-help-target={DISPATCH_LOG_FORM_HELP_KEYS.submit}
                >
                  {!dispatchLogId ? "Submit Dispatch Log" : "Update Dispatch Log"}
                </Button>
              </SheetFooter>
            </form>
          </Form>
        )}
      </SheetContent>
    </Sheet>
  );
}
